import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { SiteProvider } from "@/context/SiteContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/topbar/TopBar";
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ServicesPage from "@/pages/ServicesPage";
import InsurancePage from "@/pages/InsurancePage";
import SolutionPage from "@/pages/SolutionPage";
import ContactPage from "@/pages/ContactPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import FlatsomeExportPage from "@/pages/FlatsomeExportPage";
import DeployEnginePage from "@/pages/DeployEnginePage";
import ContentQAPage from "@/pages/ContentQAPage";
import WorkspacePage from "@/pages/WorkspacePage";
import SitePreviewPage from "@/pages/SitePreviewPage";
import DataSyncPage from "@/pages/DataSyncPage";
import AAPilotPage from "@/pages/AAPilotPage";
import IndustryPage from "@/pages/IndustryPage";
import ServiceDetailPage from "@/pages/ServiceDetailPage";
import LoginPage, { WmmBrandStack } from "@/pages/LoginPage";
import SsoCallbackPage from "@/pages/SsoCallbackPage";
import SsoCheckPage from "@/pages/SsoCheckPage";
import SharePreviewPage from "@/pages/SharePreviewPage";
import VERT_DATA from "@/data/verticalsData.json";
import SVC_DATA from "@/data/servicePagesData.json";

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 350);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
    return undefined;
  }, [pathname, hash]);
  return null;
};

// Full-screen splash shown while the session is being validated.
const AuthLoadingScreen = () => (
  <div
    data-testid="auth-loading-screen"
    className="min-h-screen bg-[#fafafa] flex flex-col items-center justify-center gap-5"
    style={{ fontFamily: '"IBM Plex Sans", ui-sans-serif, system-ui, sans-serif' }}
  >
    <WmmBrandStack size={96} />
    <Loader2 size={18} className="animate-spin text-[#a8a29e]" />
  </div>
);

const RequireAuth = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <AuthLoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// The authenticated application shell (chrome + all tool/mirror routes).
const AppShell = () => (
  <SiteProvider>
    <ScrollToTop />
    <TopBar />
    {/* Offset for the fixed app chrome bar (two rows on mobile) */}
    <div className="pt-[110px] sm:pt-11">
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/insurance-marketing-machine" element={<InsurancePage />} />
        <Route path="/solution" element={<SolutionPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/flatsome-export" element={<FlatsomeExportPage />} />
        <Route path="/deploy" element={<DeployEnginePage />} />
        <Route path="/workspace" element={<WorkspacePage />} />
        <Route path="/preview" element={<SitePreviewPage />} />
        <Route path="/sync" element={<DataSyncPage />} />
        <Route path="/sso-check" element={<SsoCheckPage />} />
        <Route path="/aa-pilot" element={<AAPilotPage />} />
        <Route path="/content-qa" element={<ContentQAPage />} />
        {Object.entries(VERT_DATA.verticals).map(([k, v]) => (
          <Route key={k} path={`/${v.slug}`} element={<IndustryPage vkey={k} />} />
        ))}
        {Object.keys(SVC_DATA.services).map((k) => (
          <Route key={k} path={`/${k}`} element={<ServiceDetailPage skey={k} />} />
        ))}
      </Routes>
    </div>
  </SiteProvider>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public: cold-hit login + WMM SSO landing route */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/sso" element={<SsoCallbackPage />} />
            {/* Public: client mockup preview via per-workspace secret token */}
            <Route path="/share/:token" element={<SharePreviewPage />} />
            {/* Everything else requires a session */}
            <Route
              path="/*"
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            />
          </Routes>
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
